import "./styles.css";
import type {
  AuthResponse,
  MeetingSummaryItem,
  MeetingSummaryResponse,
  MediaState,
  ParticipantSummary,
  PublicRoomSummary,
  RoomCreateResponse,
  RoomJoinResponse,
  RecordingMode,
  RoomListResponse,
  ServerMessage,
  TtsVoiceStyle,
  UserSummary,
} from "../shared/protocol";
import type { AudioLevel } from "./audio";
import {
  isMobileBrowser,
  LocalMediaController,
  type CameraLevel,
} from "./media";
import { MeetingRecorder, type RecordingSource } from "./recording";
import {
  ScreenPresentationController,
  type HighlighterColor,
} from "./presentation";
import {
  LocalSpeechController,
} from "./speech";
import { MobileServerSpeechController } from "./mobile-speech";
import { BrowserTtsController } from "./tts";
import {
  PeerManager,
  type ConnectionQuality,
  type PeerEvents,
} from "./webrtc";
import type { VideoEffectMode } from "./vision";

const byId = <T extends HTMLElement>(id: string): T => {
  const element = document.getElementById(id);
  if (!element) throw new Error(`#${id} 요소가 없습니다.`);
  return element as T;
};

const authView = byId<HTMLElement>("auth-view");
const lobbyView = byId<HTMLElement>("lobby-view");
const checkView = byId<HTMLElement>("check-view");
const meetingView = byId<HTMLElement>("meeting-view");
const signupForm = byId<HTMLFormElement>("signup-form");
const loginForm = byId<HTMLFormElement>("login-form");
const createRoomForm = byId<HTMLFormElement>("create-room-form");
const roomTitle = byId<HTMLInputElement>("room-title");
const createButton = byId<HTMLButtonElement>("create-room");
const roomList = byId<HTMLElement>("room-list");
const refreshRoomsButton = byId<HTMLButtonElement>("refresh-rooms");
const accountActions = byId<HTMLElement>("account-actions");
const accountName = byId<HTMLElement>("account-name");
const purchaseDialog = byId<HTMLDialogElement>("purchase-dialog");
const purchaseForm = byId<HTMLFormElement>("purchase-form");
const rawVideo = byId<HTMLVideoElement>("raw-local-video");
const processedCanvas = byId<HTMLCanvasElement>("processed-local-canvas");
const previewModeLabel = byId<HTMLElement>("preview-mode-label");
const visionState = byId<HTMLElement>("vision-state");
const cameraPlaceholder = byId<HTMLElement>("camera-placeholder");
const lobbyMicMeter = byId<HTMLElement>("lobby-mic-meter");
const lobbyMicValue = byId<HTMLOutputElement>("lobby-mic-value");
const meetingMicMeter = byId<HTMLElement>("meeting-mic-meter");
const meetingMicValue = byId<HTMLOutputElement>("meeting-mic-value");
const meetingSpeakerMeter = byId<HTMLElement>("meeting-speaker-meter");
const meetingSpeakerValue = byId<HTMLOutputElement>("meeting-speaker-value");
const lobbyCameraMeter = byId<HTMLElement>("lobby-camera-meter");
const lobbyCameraValue = byId<HTMLOutputElement>("lobby-camera-value");
const meetingCameraMeter = byId<HTMLElement>("meeting-camera-meter");
const meetingCameraValue = byId<HTMLOutputElement>("meeting-camera-value");
const dialogMicMeter = byId<HTMLElement>("dialog-mic-meter");
const dialogMicValue = byId<HTMLOutputElement>("dialog-mic-value");
const dialogCameraMeter = byId<HTMLElement>("dialog-camera-meter");
const dialogCameraValue = byId<HTMLOutputElement>("dialog-camera-value");
const videoGrid = byId<HTMLElement>("video-grid");
const presentationCanvas = byId<HTMLCanvasElement>("presentation-canvas");
const presentationToolbar = byId<HTMLElement>("presentation-toolbar");
const presentationTools = byId<HTMLElement>("presentation-tools");
const presentationLabel = byId<HTMLElement>("presentation-label");
const presentationButton = byId<HTMLButtonElement>("toggle-presentation");
const highlighterButton = byId<HTMLButtonElement>("toggle-highlighter");
const meetingWorkspace = byId<HTMLElement>("meeting-workspace");
const captionOverlay = byId<HTMLElement>("caption-overlay");
const overlayCaptionSpeaker = byId<HTMLElement>("overlay-caption-speaker");
const overlayCaptionText = byId<HTMLElement>("overlay-caption-text");
const captionOverlayHome = captionOverlay.parentElement as HTMLElement;
const globalNetworkState = byId<HTMLElement>("global-network-state");
const participantCount = byId<HTMLElement>("participant-count");
const captionList = byId<HTMLElement>("caption-list");
const sttState = byId<HTMLElement>("stt-state");
const typedSpeechForm = byId<HTMLFormElement>("typed-speech-form");
const typedSpeechInput = byId<HTMLTextAreaElement>("typed-speech-input");
const typedSpeechCount = byId<HTMLOutputElement>("typed-speech-count");
const typedSpeechToggle = byId<HTMLButtonElement>("toggle-typed-speech");
const typedSpeechVoice =
  byId<HTMLSelectElement>("typed-speech-voice");
const generateSummaryButton =
  byId<HTMLButtonElement>("generate-summary");
const summaryEmpty = byId<HTMLElement>("summary-empty");
const summaryDialog = byId<HTMLDialogElement>("summary-dialog");
const summaryInputView = byId<HTMLElement>("summary-input-view");
const summaryResultView = byId<HTMLElement>("summary-result-view");
const summaryInputForm = byId<HTMLFormElement>("summary-input-form");
const summaryParticipantsInput =
  byId<HTMLInputElement>("summary-participants");
const summaryMeetingContextInput =
  byId<HTMLTextAreaElement>("summary-meeting-context");
const summaryGuidanceInput = byId<HTMLTextAreaElement>("summary-guidance");
const summaryTranscriptInput =
  byId<HTMLTextAreaElement>("summary-transcript");
const summaryTranscriptCount =
  byId<HTMLElement>("summary-transcript-count");
const summaryInputStatus = byId<HTMLElement>("summary-input-status");
const summarySubmitButton = byId<HTMLButtonElement>("summary-submit");
const editSummaryInputButton =
  byId<HTMLButtonElement>("edit-summary-input");
const regenerateSummaryButton =
  byId<HTMLButtonElement>("regenerate-summary");
const summaryEmailForm = byId<HTMLFormElement>("summary-email-form");
const summaryEmailInput = byId<HTMLInputElement>("summary-email");
const sendSummaryEmailButton =
  byId<HTMLButtonElement>("send-summary-email");
const summaryEmailStatus = byId<HTMLOutputElement>("summary-email-status");
const inspector = byId<HTMLElement>("inspector");
const toggleInspectorButton =
  byId<HTMLButtonElement>("toggle-inspector");
const closeInspectorButton =
  byId<HTMLButtonElement>("close-inspector");
const deviceDialog = byId<HTMLDialogElement>("device-dialog");
const effectsDialog = byId<HTMLDialogElement>("effects-dialog");
const timerElement = byId<HTMLElement>("meeting-timer");
const toastRegion = byId<HTMLElement>("toast-region");

type PendingMeeting = {
  roomId: string;
  token: string;
  inviteUrl: string;
  name: string;
  isHost: boolean;
  autoRecordingEnabled: boolean;
  recordingMode: RecordingMode;
};

let pendingMeeting: PendingMeeting | undefined;
let currentUser: UserSummary | undefined;
let roomRefreshTimer = 0;
let localMedia: LocalMediaController | undefined;
let peerManager: PeerManager | undefined;
type SpeechController = {
  enable(): Promise<boolean>;
  disable(): void;
  isEnabled(): boolean;
  setInputActive(active: boolean): void;
  setPlaybackActive(active: boolean): void;
  setAudioLevel?(level: AudioLevel): void;
};

let speechController: SpeechController | undefined;
let recorder: MeetingRecorder | undefined;
let meetingStartedAt = 0;
let meetingTimer = 0;
let currentAvatar = "😀";
let localParticipantId = "";
let localIsHost = false;
let typedCaptionSequence = 1_000_000_000;
let ttsUnavailableNotified = false;
let overlayCaptionTimer = 0;
let inspectorOpen = false;
let latestMeetingSummary: MeetingSummaryResponse | undefined;
let summaryTranscriptRoomId = "";
let activePresentation:
  | { presenterId: string; presenterName: string }
  | undefined;
let presentationTransition = false;
const presentationController = new ScreenPresentationController(
  presentationCanvas,
  () => {
    void stopLocalPresentation();
  },
);
const ttsController = new BrowserTtsController((playing) => {
  speechController?.setPlaybackActive(playing);
});
type DeviceSettings = {
  audioInputId: string;
  videoInputId: string;
  outputDeviceId: string;
  inputGain: number;
  outputVolume: number;
  channelMode: "mono" | "stereo";
};
const deviceSettings: DeviceSettings = loadDeviceSettings();
let currentMicLevel: AudioLevel = {
  linear: 0,
  dbfs: -100,
  clipping: false,
  silent: true,
};
const participants = new Map<string, ParticipantSummary>();
const tiles = new Map<
  string,
  {
    root: HTMLElement;
    video?: HTMLVideoElement;
    placeholder: HTMLElement;
    audio: HTMLElement;
  }
>();
const remoteLevels = new Map<string, AudioLevel>();
const captionElements = new Map<string, HTMLElement>();
const captionSpeakerTimers = new Map<string, number>();
const spokenTypedCaptions = new Set<string>();

const pathMatch = /^\/r\/([A-Z0-9_-]+)$/i.exec(location.pathname);
const invitedRoomId = pathMatch?.[1]?.toUpperCase() ?? "";

let viewportSyncFrame = 0;
const syncViewportHeight = (): void => {
  window.cancelAnimationFrame(viewportSyncFrame);
  viewportSyncFrame = window.requestAnimationFrame(() => {
    const viewport = window.visualViewport;
    const activeElement = document.activeElement;
    const isTextEntry =
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement ||
      (activeElement instanceof HTMLElement && activeElement.isContentEditable);
    const visibleBottom = viewport
      ? viewport.height + viewport.offsetTop
      : window.innerHeight;
    const keyboardVisible =
      isTextEntry && window.innerHeight - visibleBottom > 120;
    const height = keyboardVisible ? visibleBottom : window.innerHeight;
    document.documentElement.style.setProperty(
      "--app-height",
      `${Math.round(height)}px`,
    );
  });
};

syncViewportHeight();
window.addEventListener("resize", syncViewportHeight, { passive: true });
window.addEventListener("orientationchange", syncViewportHeight, {
  passive: true,
});
window.visualViewport?.addEventListener("resize", syncViewportHeight, {
  passive: true,
});
window.visualViewport?.addEventListener("scroll", syncViewportHeight, {
  passive: true,
});

for (const button of document.querySelectorAll<HTMLButtonElement>(
  "[data-auth-mode]",
)) {
  button.addEventListener("click", () => {
    const signup = button.dataset.authMode === "signup";
    signupForm.hidden = !signup;
    loginForm.hidden = signup;
    for (const tab of document.querySelectorAll<HTMLButtonElement>(
      "[data-auth-mode]",
    )) {
      tab.classList.toggle("active", tab === button);
      tab.setAttribute("aria-selected", String(tab === button));
    }
    (signup
      ? byId<HTMLInputElement>("signup-email")
      : byId<HTMLInputElement>("login-email")
    ).focus();
  });
}

signupForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void submitAuth("signup");
});
loginForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void submitAuth("login");
});
createRoomForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void createRoom();
});
refreshRoomsButton.addEventListener("click", () => {
  void loadRooms();
});
roomList.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    "button[data-room-id]",
  );
  if (button?.dataset.roomId) {
    void joinRoom(button.dataset.roomId);
  }
});
byId<HTMLButtonElement>("logout-button").addEventListener("click", () => {
  void logout();
});
byId<HTMLButtonElement>("open-purchase").addEventListener("click", () => {
  purchaseDialog.showModal();
});
purchaseDialog.addEventListener("click", (event) => {
  if (event.target === purchaseDialog) purchaseDialog.close();
});
purchaseForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void submitPurchaseInquiry();
});
byId<HTMLButtonElement>("back-to-lobby").addEventListener("click", () => {
  localMedia?.stop();
  localMedia = undefined;
  pendingMeeting = undefined;
  history.replaceState({}, "", "/");
  showView("lobby");
  void loadRooms();
});
byId<HTMLButtonElement>("test-speaker").addEventListener("click", () => {
  void localMedia?.testSpeaker();
});
byId<HTMLButtonElement>("enter-meeting").addEventListener("click", () => {
  void enterMeetingImmersively();
});
byId<HTMLButtonElement>("copy-invite").addEventListener("click", () => {
  void copyInvite();
});
byId<HTMLButtonElement>("download-transcript").addEventListener("click", () => {
  void downloadTranscript();
});
generateSummaryButton.addEventListener("click", () => {
  void openMeetingSummaryDialog();
});
summaryDialog.addEventListener("click", (event) => {
  if (event.target === summaryDialog) summaryDialog.close();
});
summaryInputForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void generateMeetingSummary();
});
editSummaryInputButton.addEventListener("click", () => {
  setSummaryDialogView("input");
  summaryGuidanceInput.focus();
});
regenerateSummaryButton.addEventListener("click", () => {
  void generateMeetingSummary();
});
summaryEmailForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void sendMeetingSummaryEmail();
});
summaryTranscriptInput.addEventListener("input", () => {
  summaryTranscriptCount.textContent =
    `직접 편집 · ${summaryTranscriptInput.value.length.toLocaleString("ko-KR")}자`;
});
byId<HTMLButtonElement>("leave-meeting").addEventListener("click", () => {
  void leaveMeeting();
});
presentationButton.addEventListener("click", () => {
  if (activePresentation?.presenterId === localParticipantId) {
    void stopLocalPresentation();
  } else {
    void startLocalPresentation();
  }
});
byId<HTMLButtonElement>("stop-presentation").addEventListener("click", () => {
  void stopLocalPresentation();
});
highlighterButton.addEventListener("click", () => {
  const enabled = !presentationController.isMarkerEnabled();
  presentationController.setMarkerEnabled(enabled);
  highlighterButton.setAttribute("aria-pressed", String(enabled));
  highlighterButton.textContent = enabled ? "형광펜 켬" : "형광펜";
});
byId<HTMLButtonElement>("clear-highlighter").addEventListener("click", () => {
  presentationController.clearAnnotations();
  toast("화면의 마킹을 모두 지웠습니다.");
});
for (const button of document.querySelectorAll<HTMLButtonElement>(
  "[data-highlighter-color]",
)) {
  button.addEventListener("click", () => {
    const color = button.dataset.highlighterColor as HighlighterColor;
    presentationController.setMarkerColor(color);
    presentationController.setMarkerEnabled(true);
    highlighterButton.setAttribute("aria-pressed", "true");
    highlighterButton.textContent = "형광펜 켬";
    for (const candidate of document.querySelectorAll(
      "[data-highlighter-color]",
    )) {
      candidate.classList.toggle("active", candidate === button);
    }
  });
}
typedSpeechForm.addEventListener("submit", (event) => {
  event.preventDefault();
  sendTypedSpeech();
});
typedSpeechInput.addEventListener("input", () => {
  typedSpeechCount.textContent = `${typedSpeechInput.value.length}/500`;
});
typedSpeechInput.addEventListener("keydown", (event) => {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    typedSpeechForm.requestSubmit();
  }
});
typedSpeechToggle.addEventListener("click", () => {
  ttsController.setEnabled(!ttsController.isEnabled());
  updateTtsToggle();
});
typedSpeechVoice.value = ttsController.getVoiceStyle();
typedSpeechVoice.addEventListener("change", () => {
  const style = typedSpeechVoice.value as TtsVoiceStyle;
  ttsController.setVoiceStyle(style);
  toast(
    style === "male"
      ? "텍스트를 남성 음성으로 전달합니다."
      : style === "female"
        ? "텍스트를 여성 음성으로 전달합니다."
        : "텍스트 음성을 자동으로 선택합니다.",
  );
});
updateTtsToggle();
syncDeviceControls();
renderPresentationState();

const setInspectorOpen = (open: boolean): void => {
  inspectorOpen = open;
  meetingWorkspace.classList.toggle("inspector-open", open);
  inspector.classList.toggle("mobile-open", open);
  toggleInspectorButton.classList.toggle("active", open);
  toggleInspectorButton.setAttribute("aria-expanded", String(open));
  toggleInspectorButton.setAttribute("aria-pressed", String(open));
};

toggleInspectorButton.addEventListener("click", () => {
  setInspectorOpen(!inspectorOpen);
});
closeInspectorButton.addEventListener("click", () => {
  setInspectorOpen(false);
  toggleInspectorButton.focus();
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && inspectorOpen) {
    setInspectorOpen(false);
    toggleInspectorButton.focus();
  }
});

for (const controlId of ["effect-control", "meeting-effect-control"]) {
  byId<HTMLElement>(controlId).addEventListener("click", (event) => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "button[data-effect]",
    );
    if (!target) return;
    setEffect(target.dataset.effect as VideoEffectMode);
  });
}

for (const rowId of ["avatar-row", "meeting-avatar-row"]) {
  byId<HTMLElement>(rowId).addEventListener("click", (event) => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "button[data-avatar]",
    );
    if (!target?.dataset.avatar) return;
    selectAvatar(target.dataset.avatar);
  });
}

byId<HTMLButtonElement>("open-device-settings").addEventListener("click", () => {
  syncDeviceControls();
  deviceDialog.showModal();
});
byId<HTMLButtonElement>("dialog-test-speaker").addEventListener("click", () => {
  void localMedia?.testSpeaker();
});
byId<HTMLButtonElement>("toggle-effects").addEventListener("click", () => {
  syncEffectControls();
  effectsDialog.showModal();
});

for (const select of document.querySelectorAll<HTMLSelectElement>(
  "[data-device-select]",
)) {
  select.addEventListener("change", () => {
    void changeDevice(select.dataset.deviceSelect ?? "", select.value);
  });
}
for (const control of document.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
  "[data-device-setting]",
)) {
  control.addEventListener("input", () => {
    void changeDeviceSetting(control.dataset.deviceSetting ?? "", control.value);
  });
  control.addEventListener("change", () => {
    void changeDeviceSetting(control.dataset.deviceSetting ?? "", control.value);
  });
}

for (const button of document.querySelectorAll<HTMLButtonElement>(
  ".inspector-tabs button",
)) {
  button.addEventListener("click", () => {
    for (const tab of document.querySelectorAll(".inspector-tabs button")) {
      tab.classList.toggle("active", tab === button);
    }
    for (const panel of document.querySelectorAll(".inspector-panel")) {
      panel.classList.toggle(
        "active",
        panel.id === `${button.dataset.panel}-panel`,
      );
    }
  });
}

byId<HTMLButtonElement>("toggle-mic").addEventListener("click", () => {
  if (!localMedia) return;
  if (!localMedia.hasAudio()) {
    toast("사용 가능한 마이크가 없습니다. 텍스트로 말하기를 이용하세요.", true);
    return;
  }
  const enabled = !localMedia.isAudioEnabled();
  localMedia.setAudioEnabled(enabled);
  speechController?.setInputActive(enabled);
  setControlState(byId("toggle-mic"), enabled);
  broadcastMediaState();
});

byId<HTMLButtonElement>("toggle-camera").addEventListener("click", () => {
  if (!localMedia) return;
  if (!localMedia.hasVideo()) {
    toast("사용 가능한 카메라가 없습니다.", true);
    return;
  }
  const enabled = !localMedia.isVideoEnabled();
  localMedia.setVideoEnabled(enabled);
  setControlState(byId("toggle-camera"), enabled);
  cameraPlaceholder.hidden = enabled;
  broadcastMediaState();
});

byId<HTMLButtonElement>("toggle-captions").addEventListener("click", () => {
  void toggleCaptions();
});

window.addEventListener("beforeunload", () => {
  recorder?.flush();
  speechController?.disable();
  presentationController.stop();
  peerManager?.close();
  localMedia?.stop();
  ttsController.stop();
});

void initialize();

async function initialize(): Promise<void> {
  try {
    const response = await fetch("/api/auth/me");
    if (!response.ok) {
      showView("auth");
      return;
    }
    const payload = (await response.json()) as AuthResponse;
    currentUser = payload.user;
    await openLobby();
  } catch {
    showView("auth");
    toast("서버에 연결하지 못했습니다. 잠시 후 새로고침해 주세요.", true);
  }
}

async function submitAuth(mode: "signup" | "login"): Promise<void> {
  const form = mode === "signup" ? signupForm : loginForm;
  const fields = new FormData(form);
  const email = String(fields.get("email") ?? "").trim();
  const password = String(fields.get("password") ?? "");
  const name = String(fields.get("name") ?? "").replace(/\s+/g, " ").trim();
  if (!email || !password || (mode === "signup" && !name)) {
    toast("메일주소, 이름, 비밀번호를 모두 입력하세요.", true);
    return;
  }
  if (mode === "signup" && password.length < 8) {
    toast("비밀번호는 8자 이상 입력하세요.", true);
    return;
  }
  setAuthBusy(true);
  try {
    const response = await fetch(`/api/auth/${mode}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        ...(mode === "signup" ? { name } : {}),
      }),
    });
    const payload = (await response.json()) as AuthResponse & {
      error?: string;
    };
    if (!response.ok) {
      throw new Error(payload.error ?? "회원 정보를 확인하지 못했습니다.");
    }
    currentUser = payload.user;
    toast(mode === "signup" ? "가입했습니다. 바로 시험해 보세요." : "로그인했습니다.");
    await openLobby();
  } catch (error) {
    toast(errorMessage(error), true);
  } finally {
    setAuthBusy(false);
  }
}

function setAuthBusy(busy: boolean): void {
  for (const button of document.querySelectorAll<HTMLButtonElement>(
    ".auth-form button[type='submit']",
  )) {
    button.disabled = busy;
  }
}

async function openLobby(): Promise<void> {
  if (!currentUser) return;
  accountName.textContent = currentUser.name;
  accountActions.hidden = false;
  showView("lobby");
  await loadRooms();
  window.clearInterval(roomRefreshTimer);
  roomRefreshTimer = window.setInterval(() => {
    if (!lobbyView.hidden) void loadRooms(true);
  }, 10_000);
}

async function loadRooms(quiet = false): Promise<void> {
  refreshRoomsButton.disabled = true;
  if (!quiet) {
    roomList.innerHTML =
      '<div class="room-list-state"><span class="loading-orbit"></span><p>열려 있는 방을 확인하고 있습니다.</p></div>';
  }
  try {
    const response = await fetch("/api/rooms");
    const payload = (await response.json()) as RoomListResponse & {
      error?: string;
    };
    if (response.status === 401) {
      currentUser = undefined;
      accountActions.hidden = true;
      showView("auth");
      return;
    }
    if (!response.ok) {
      throw new Error(payload.error ?? "방 목록을 불러오지 못했습니다.");
    }
    renderRooms(payload.rooms);
  } catch (error) {
    if (!quiet) {
      roomList.innerHTML = "";
      const state = document.createElement("div");
      state.className = "room-list-state error";
      const message = document.createElement("p");
      message.textContent = errorMessage(error);
      const retry = document.createElement("button");
      retry.type = "button";
      retry.textContent = "다시 불러오기";
      retry.addEventListener("click", () => void loadRooms());
      state.append(message, retry);
      roomList.append(state);
    }
  } finally {
    refreshRoomsButton.disabled = false;
  }
}

function renderRooms(rooms: PublicRoomSummary[]): void {
  roomList.replaceChildren();
  if (rooms.length === 0) {
    const state = document.createElement("div");
    state.className = "room-list-state empty";
    const mark = document.createElement("span");
    mark.textContent = "+";
    const title = document.createElement("strong");
    title.textContent = "아직 열린 방이 없습니다.";
    const copy = document.createElement("p");
    copy.textContent = "첫 공개 시험 방을 열어 다른 사용자를 기다려 보세요.";
    const action = document.createElement("button");
    action.type = "button";
    action.textContent = "방 이름 입력하기";
    action.addEventListener("click", () => roomTitle.focus());
    state.append(mark, title, copy, action);
    roomList.append(state);
    return;
  }

  for (const room of rooms) {
    const card = document.createElement("article");
    card.className = "room-card";
    if (room.id === invitedRoomId) card.classList.add("invited");
    const action = document.createElement("button");
    action.type = "button";
    action.dataset.roomId = room.id;
    action.disabled = room.participantCount >= room.capacity;
    action.setAttribute(
      "aria-label",
      `${room.title} 방 ${room.participantCount}명 참여 중`,
    );

    const head = document.createElement("div");
    head.className = "room-card-head";
    const status = document.createElement("span");
    status.className = "room-live";
    status.innerHTML = "<i></i> OPEN";
    const count = document.createElement("span");
    count.className = "room-count";
    count.textContent = `${room.participantCount} / ${room.capacity}명`;
    head.append(status, count);

    const title = document.createElement("h3");
    title.textContent = room.title;
    const meta = document.createElement("p");
    meta.textContent = `${room.hostName} 님이 ${relativeTime(room.createdAt)} 개설`;
    const enter = document.createElement("span");
    enter.className = "room-enter";
    enter.textContent =
      room.participantCount >= room.capacity ? "정원이 찼습니다" : "눌러서 참여 →";
    action.append(head, title, meta, enter);
    card.append(action);
    roomList.append(card);
  }

  const invited = roomList.querySelector(".room-card.invited");
  invited?.scrollIntoView({ block: "center", behavior: "smooth" });
}

function relativeTime(iso: string): string {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / 60_000),
  );
  if (minutes < 1) return "방금";
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  return new Date(iso).toLocaleDateString("ko-KR");
}

async function logout(): Promise<void> {
  try {
    await fetch("/api/auth/logout", { method: "POST" });
  } finally {
    currentUser = undefined;
    pendingMeeting = undefined;
    signupForm.reset();
    loginForm.reset();
    createRoomForm.reset();
    accountActions.hidden = true;
    window.clearInterval(roomRefreshTimer);
    history.replaceState({}, "", "/");
    showView("auth");
    toast("로그아웃했습니다.");
  }
}

async function submitPurchaseInquiry(): Promise<void> {
  if (!currentUser) {
    purchaseDialog.close();
    showView("auth");
    return;
  }
  const submit = purchaseForm.querySelector<HTMLButtonElement>(
    "button[type='submit']",
  );
  if (!submit) return;
  const fields = new FormData(purchaseForm);
  submit.disabled = true;
  submit.textContent = "담당자에게 전달 중…";
  try {
    const response = await fetch("/api/purchase-inquiries", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        company: String(fields.get("company") ?? "").trim(),
        phone: String(fields.get("phone") ?? "").trim(),
        message: String(fields.get("message") ?? "").trim(),
      }),
    });
    const payload = (await response.json()) as {
      message?: string;
      error?: string;
    };
    if (!response.ok) {
      throw new Error(payload.error ?? "구매 상담 요청을 보내지 못했습니다.");
    }
    purchaseForm.reset();
    purchaseDialog.close();
    toast(payload.message ?? "구매 상담 요청을 전달했습니다.");
  } catch (error) {
    toast(errorMessage(error), true);
  } finally {
    submit.disabled = false;
    submit.textContent = "구매 상담 요청 보내기";
  }
}

async function createRoom(): Promise<void> {
  if (!currentUser) {
    showView("auth");
    return;
  }
  const title = roomTitle.value.replace(/\s+/g, " ").trim();
  if (!title) {
    roomTitle.focus();
    toast("공개 방의 이름을 입력하세요.", true);
    return;
  }
  setEntryBusy(true);
  try {
    const response = await fetch("/api/rooms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title }),
    });
    const payload = (await response.json()) as RoomCreateResponse & {
      error?: string;
    };
    if (!response.ok) throw new Error(payload.error ?? "회의실 생성 실패");
    pendingMeeting = {
      roomId: payload.roomId,
      token: payload.hostToken,
      inviteUrl: payload.joinUrl,
      name: currentUser.name,
      isHost: true,
      autoRecordingEnabled: payload.autoRecordingEnabled,
      recordingMode: payload.recordingMode,
    };
    createRoomForm.reset();
    history.replaceState({}, "", new URL(payload.joinUrl).pathname);
    await prepareDevices();
  } catch (error) {
    toast(errorMessage(error), true);
  } finally {
    setEntryBusy(false);
  }
}

async function joinRoom(targetRoom: string): Promise<void> {
  if (!currentUser) {
    showView("auth");
    return;
  }
  setEntryBusy(true);
  try {
    const response = await fetch(
      `/api/rooms/${encodeURIComponent(targetRoom)}/join`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      },
    );
    const payload = (await response.json()) as RoomJoinResponse & {
      error?: string;
    };
    if (!response.ok) throw new Error(payload.error ?? "회의 참여 실패");
    pendingMeeting = {
      roomId: targetRoom,
      token: payload.participantToken,
      inviteUrl: `${location.origin}/r/${targetRoom}`,
      name: currentUser.name,
      isHost: false,
      autoRecordingEnabled: payload.autoRecordingEnabled,
      recordingMode: payload.recordingMode,
    };
    history.replaceState({}, "", `/r/${targetRoom}`);
    await prepareDevices();
  } catch (error) {
    toast(errorMessage(error), true);
  } finally {
    setEntryBusy(false);
  }
}

async function prepareDevices(): Promise<void> {
  if (!pendingMeeting) return;
  localStorage.setItem("softi-meet-display-name", pendingMeeting.name);
  showView("check");
  localMedia = new LocalMediaController(
    rawVideo,
    processedCanvas,
    (state) => {
      visionState.textContent = state;
    },
    updateMicLevel,
    updateCameraLevel,
  );
  localMedia.setAvatar(currentAvatar);
  localMedia.setInputGain(deviceSettings.inputGain);
  localMedia.setOutputVolume(deviceSettings.outputVolume);
  localMedia.setOutputDevice(deviceSettings.outputDeviceId);
  await localMedia.setChannelMode(deviceSettings.channelMode);
  let mediaError: unknown;
  try {
    await localMedia.start();
  } catch (error) {
    mediaError = error;
    localMedia.startWithoutDevices();
  }
  if (!mediaError && deviceSettings.audioInputId) {
    try {
      await localMedia.switchAudioInput(deviceSettings.audioInputId);
    } catch {
      deviceSettings.audioInputId = "";
      saveDeviceSettings();
    }
  }
  if (!mediaError && deviceSettings.videoInputId) {
    try {
      await localMedia.switchVideoInput(deviceSettings.videoInputId);
    } catch {
      deviceSettings.videoInputId = "";
      saveDeviceSettings();
    }
  }
  await refreshDeviceLists();

  const hasAudio = localMedia.hasAudio();
  const hasVideo = localMedia.hasVideo();
  previewModeLabel.textContent = hasVideo ? "원본 영상" : "카메라 없이 입장";
  visionState.textContent = hasVideo ? "로컬 원본 처리" : "카메라 사용 안 함";
  cameraPlaceholder.hidden = hasVideo;
  setMediaAvailability(hasAudio, hasVideo);

  if (mediaError) {
    const message =
      mediaError instanceof DOMException && mediaError.name === "NotAllowedError"
        ? "카메라·마이크 권한이 꺼져 있습니다."
        : "카메라와 마이크를 시작하지 못했습니다.";
    toast(`${message} 미디어 없이도 회의에 입장할 수 있습니다.`, true);
  } else if (!hasAudio || !hasVideo) {
    const missing = [!hasAudio ? "마이크" : "", !hasVideo ? "카메라" : ""]
      .filter(Boolean)
      .join("와 ");
    toast(`${missing} 없이 사용 가능한 장치만으로 계속합니다.`);
  }
}

async function enterMeeting(): Promise<void> {
  if (!pendingMeeting || !localMedia) return;
  const enterButton = byId<HTMLButtonElement>("enter-meeting");
  enterButton.disabled = true;
  enterButton.textContent = "연결하는 중…";
  participants.clear();
  tiles.clear();
  videoGrid.replaceChildren();
  captionElements.clear();
  spokenTypedCaptions.clear();
  captionList.innerHTML =
    '<div class="empty-state"><span>“</span><p>발언이 시작되면<br>여기에 자막이 표시됩니다.</p></div>';
  resetMeetingSummary();
  activePresentation = undefined;
  renderPresentationState();

  try {
    peerManager = new PeerManager(
      localMedia.getSendStream(),
      createPeerEvents(),
    );
    await peerManager.connect(pendingMeeting.token);
    showView("meeting");
    setInspectorOpen(true);
    hideCaptionOverlay();
    createLocalTile();
    byId<HTMLElement>("meeting-room-code").textContent = pendingMeeting.roomId;
    meetingStartedAt = Date.now();
    window.clearInterval(meetingTimer);
    updateMeetingTimer();
    meetingTimer = window.setInterval(updateMeetingTimer, 1_000);
    startSpeechRecognition();
    broadcastMediaState();
    updateParticipantCount();
    if (
      pendingMeeting.autoRecordingEnabled &&
      (pendingMeeting.recordingMode === "per_participant" || localIsHost)
    ) {
      void startAutomaticRecording();
    }
  } catch (error) {
    speechController?.disable();
    speechController = undefined;
    peerManager?.close();
    peerManager = undefined;
    toast(errorMessage(error), true);
  } finally {
    enterButton.disabled = false;
    enterButton.textContent = "회의실 입장";
  }
}

function startSpeechRecognition(): void {
  if (!localMedia || !pendingMeeting || speechController) return;
  const captionHandler = (text: string, final: boolean, sequence: number) => {
    peerManager?.sendCaption(text, final, sequence);
  };
  const stateHandler = (state: string, enabled: boolean) => {
    sttState.textContent = state;
    updateCaptionToggle(enabled);
  };

  if (isMobileBrowser()) {
    const stream = localMedia.getRawStream();
    if (!stream) {
      stateHandler("사용 가능한 모바일 자막 마이크가 없습니다", false);
      return;
    }
    const controller = new MobileServerSpeechController(
      stream,
      pendingMeeting.roomId,
      pendingMeeting.token,
      captionHandler,
      stateHandler,
    );
    controller.setInputActive(localMedia.isAudioEnabled());
    controller.setPlaybackActive(ttsController.isPlaying());
    speechController = controller;
    void controller.enable();
    return;
  }

  const controller = new LocalSpeechController(
    captionHandler,
    stateHandler,
  );
  controller.setInputActive(localMedia.isAudioEnabled());
  controller.setPlaybackActive(ttsController.isPlaying());
  speechController = controller;
  void controller.enable().then((enabled) => {
    if (!enabled && speechController === controller) {
      void controller.check();
    }
  });
}

async function enterMeetingImmersively(): Promise<void> {
  if (isMobileBrowser()) {
    // 모바일에서는 전체화면 요청이 끝나지 않아도 회의 연결과 서버 자막을
    // 시작할 수 있어야 합니다.
    const meetingEntry = enterMeeting();
    void requestImmersiveMode();
    await meetingEntry;
    return;
  }
  // 전체화면은 회의 연결의 필수 조건이 아닙니다. 일부 모바일 브라우저는
  // requestFullscreen()의 Promise를 끝내지 않으므로 입장을 막지 않게 합니다.
  void requestImmersiveMode();
  await enterMeeting();
}

function createPeerEvents(): PeerEvents {
  return {
    onWelcome: ({ selfId, selfName, isHost, participants: existing }) => {
      localParticipantId = selfId;
      localIsHost = isHost;
      generateSummaryButton.hidden = !isHost;
      if (pendingMeeting) {
        participants.set(selfId, {
          id: selfId,
          name: selfName,
          joinedAt: new Date().toISOString(),
          isHost,
          media: currentMediaState(),
        });
      }
      for (const participant of existing) {
        participants.set(participant.id, participant);
        createRemoteTile(participant);
      }
      updateParticipantCount();
      void loadMeetingSummary();
    },
    onParticipantJoined: (participant) => {
      participants.set(participant.id, participant);
      createRemoteTile(participant);
      updateParticipantCount();
      toast(`${participant.name} 님이 입장했습니다.`);
    },
    onParticipantLeft: (participantId) => {
      const name = participants.get(participantId)?.name ?? "참가자";
      participants.delete(participantId);
      tiles.get(participantId)?.root.remove();
      tiles.delete(participantId);
      remoteLevels.delete(participantId);
      if (activePresentation?.presenterId === participantId) {
        activePresentation = undefined;
        void restoreCameraAfterPresentation();
      }
      updateParticipantCount();
      toast(`${name} 님이 나갔습니다.`);
    },
    onRemoteStream: (participantId, stream) => {
      const tile = tiles.get(participantId);
      if (!tile?.video) return;
      tile.video.srcObject = stream;
      applyOutputSettings(tile.video);
      void tile.video.play().catch(() => {
        toast("원격 소리를 재생하려면 화면을 한 번 클릭하세요.");
      });
      tile.placeholder.hidden =
        activePresentation?.presenterId === participantId ||
        (participants.get(participantId)?.media.video ?? false);
    },
    onRemoteLevel: (participantId, level) => {
      remoteLevels.set(participantId, level);
      const tile = tiles.get(participantId);
      if (tile) {
        tile.audio.style.setProperty(
          "--level",
          `${Math.round(level.linear * 100)}%`,
        );
        tile.root.classList.toggle("speaking", level.dbfs > -38);
      }
      updateSpeakerAggregate();
    },
    onMediaState: (participantId, media) => {
      const participant = participants.get(participantId);
      if (participant) participant.media = media;
      const tile = tiles.get(participantId);
      if (tile) {
        tile.placeholder.hidden =
          activePresentation?.presenterId === participantId || media.video;
        const small = tile.placeholder.querySelector("small");
        if (small) {
          small.textContent = media.video ? "연결 중" : "카메라 꺼짐";
        }
      }
    },
    onCaption: (entry) => {
      renderCaption(entry);
      if (entry.final && entry.source === "typed") {
        const key = `${entry.from}:${entry.sequence}`;
        if (!spokenTypedCaptions.has(key)) {
          spokenTypedCaptions.add(key);
          void speakTypedCaption(entry.text, entry.voiceStyle);
        }
      }
    },
    onRecordingState: (state) => {
      if (!localIsHost) {
        toast(
          state.active
            ? "회의 안전 기록이 자동으로 시작되었습니다."
            : "회의 기록 저장이 종료되었습니다.",
        );
      }
    },
    onPresentationState: (state) => {
      void applyPresentationState(state);
    },
    onQuality: updateQuality,
    onNetworkState: updateNetworkState,
    onRoomClosed: exitClosedMeeting,
    onError: (message) => toast(message, true),
  };
}

function exitClosedMeeting(message: string): void {
  speechController?.disable();
  speechController = undefined;
  ttsController.stop();
  presentationController.stop();
  activePresentation = undefined;
  localMedia?.stop();
  localMedia = undefined;
  peerManager = undefined;
  pendingMeeting = undefined;
  window.clearInterval(meetingTimer);
  window.clearTimeout(overlayCaptionTimer);
  history.replaceState({}, "", "/");
  showView("lobby");
  void loadRooms();
  toast(message, true);
}

function createLocalTile(): void {
  if (!pendingMeeting) return;
  const localName =
    participants.get(localParticipantId)?.name ?? pendingMeeting.name;
  const tile = document.createElement("article");
  tile.className = "video-tile local";
  tile.dataset.participantId = localParticipantId;
  tile.append(processedCanvas);
  const placeholder = createTilePlaceholder(localName);
  const videoEnabled = localMedia?.isVideoEnabled() ?? false;
  placeholder.hidden = videoEnabled;
  const placeholderDetail = placeholder.querySelector("small");
  if (placeholderDetail && !videoEnabled) {
    placeholderDetail.textContent = "카메라 꺼짐";
  }
  tile.append(placeholder);
  const meta = createTileMeta(`${localName} · 나`);
  tile.append(meta.root);
  videoGrid.prepend(tile);
  tiles.set(localParticipantId, {
    root: tile,
    placeholder,
    audio: meta.audio,
  });
  updateVideoGrid();
}

function createRemoteTile(participant: ParticipantSummary): void {
  if (tiles.has(participant.id)) return;
  const tile = document.createElement("article");
  tile.className = "video-tile";
  tile.dataset.participantId = participant.id;
  const video = document.createElement("video");
  video.autoplay = true;
  video.playsInline = true;
  const placeholder = createTilePlaceholder(participant.name);
  placeholder.hidden = participant.media.video;
  const meta = createTileMeta(
    `${participant.name}${participant.isHost ? " · 호스트" : ""}`,
  );
  tile.append(video, placeholder, meta.root);
  videoGrid.append(tile);
  tiles.set(participant.id, {
    root: tile,
    video,
    placeholder,
    audio: meta.audio,
  });
  updateVideoGrid();
}

function createTilePlaceholder(name: string): HTMLElement {
  const placeholder = document.createElement("div");
  placeholder.className = "tile-placeholder";
  const initials = document.createElement("span");
  initials.textContent = name.trim().slice(0, 2).toUpperCase() || "ME";
  const detail = document.createElement("small");
  detail.textContent = "연결 중";
  placeholder.append(initials, detail);
  return placeholder;
}

function createTileMeta(name: string): { root: HTMLElement; audio: HTMLElement } {
  const root = document.createElement("div");
  root.className = "tile-meta";
  const label = document.createElement("span");
  label.className = "tile-name";
  label.textContent = name;
  const audio = document.createElement("span");
  audio.className = "tile-audio";
  audio.textContent = "AUDIO";
  const meter = document.createElement("i");
  audio.append(meter);
  root.append(label, audio);
  return { root, audio };
}

function renderCaption(
  entry: Extract<ServerMessage, { type: "caption" }>,
): void {
  captionList.querySelector(".empty-state")?.remove();
  const speakerName = resolveParticipantName(entry.from, entry.name);
  const key = `${entry.from}:${entry.sequence}`;
  let root = captionElements.get(key);
  if (!root) {
    root = document.createElement("article");
    root.className = "caption-entry";
    const header = document.createElement("header");
    const speaker = document.createElement("strong");
    speaker.dataset.speakerId = entry.from;
    speaker.textContent = speakerName;
    if (entry.source === "typed") {
      const source = document.createElement("span");
      source.className = "caption-source";
      source.textContent = "입력 음성";
      speaker.append(source);
      root.classList.add("typed");
    }
    const time = document.createElement("time");
    time.dateTime = entry.at;
    time.textContent = new Date(entry.at).toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    header.append(speaker, time);
    const paragraph = document.createElement("p");
    root.append(header, paragraph);
    captionList.append(root);
    captionElements.set(key, root);
  }
  const speaker = root.querySelector<HTMLElement>("strong");
  if (speaker?.firstChild) speaker.firstChild.textContent = speakerName;
  root.querySelector("p")!.textContent = entry.text;
  root.classList.toggle("interim", !entry.final);
  captionList.scrollTop = captionList.scrollHeight;
  showCaptionOverlay(entry.from, speakerName, entry.text, entry.final);
  markCaptionSpeaker(entry.from);
}

async function toggleCaptions(): Promise<void> {
  if (!speechController) return;
  try {
    if (speechController.isEnabled()) {
      speechController.disable();
    } else if (!(await speechController.enable())) {
      toast("이 브라우저에서 한국어 자막을 시작하지 못했습니다.", true);
    }
  } catch (error) {
    toast(errorMessage(error), true);
  }
}

async function startLocalPresentation(): Promise<void> {
  if (!peerManager || !localMedia || !localParticipantId) return;
  if (!canStartScreenPresentation()) {
    toast("모바일에서는 다른 사람의 화면 공유를 볼 수만 있습니다.", true);
    return;
  }
  if (activePresentation) {
    toast(`${activePresentation.presenterName} 님의 화면 공유가 진행 중입니다.`, true);
    return;
  }
  if (!navigator.mediaDevices?.getDisplayMedia) {
    toast("이 브라우저는 화면 공유를 지원하지 않습니다.", true);
    return;
  }
  presentationTransition = true;
  renderPresentationState();
  try {
    const track = await presentationController.start();
    const presenterName =
      participants.get(localParticipantId)?.name ?? pendingMeeting?.name ?? "나";
    activePresentation = {
      presenterId: localParticipantId,
      presenterName,
    };
    await peerManager.setOutgoingVideoTrack(track);
    renderPresentationState();
    peerManager.sendPresentationState(true);
  } catch (error) {
    presentationController.stop();
    activePresentation = undefined;
    const cameraTrack = localMedia.getSendStream().getVideoTracks()[0] ?? null;
    await peerManager.setOutgoingVideoTrack(cameraTrack);
    if (!(error instanceof DOMException && error.name === "NotAllowedError")) {
      toast(`화면 공유를 시작하지 못했습니다. ${errorMessage(error)}`, true);
    }
  } finally {
    presentationTransition = false;
    renderPresentationState();
  }
}

async function stopLocalPresentation(): Promise<void> {
  if (
    presentationTransition ||
    activePresentation?.presenterId !== localParticipantId
  ) {
    return;
  }
  presentationTransition = true;
  renderPresentationState();
  peerManager?.sendPresentationState(false);
}

async function applyPresentationState(
  state: Extract<ServerMessage, { type: "presentation-state" }>,
): Promise<void> {
  if (!peerManager || !localMedia) return;
  if (!state.active || !state.presenterId || !state.presenterName) {
    const wasPresenting =
      Boolean(activePresentation) || presentationController.isActive();
    activePresentation = undefined;
    presentationTransition = false;
    await restoreCameraAfterPresentation();
    if (wasPresenting) {
      toast("화면 공유가 끝나 카메라 영상으로 돌아왔습니다.");
    }
    return;
  }

  activePresentation = {
    presenterId: state.presenterId,
    presenterName: state.presenterName,
  };
  presentationTransition = false;
  if (state.presenterId === localParticipantId) {
    const track = presentationController.getOutputTrack();
    if (!track) {
      peerManager.sendPresentationState(false);
      return;
    }
    await peerManager.setOutgoingVideoTrack(track);
  } else {
    if (presentationController.isActive()) presentationController.stop();
    await peerManager.setOutgoingVideoTrack(null);
  }
  renderPresentationState();
  if (state.presenterId !== localParticipantId) {
    toast(`${state.presenterName} 님이 화면 공유를 시작했습니다.`);
  }
}

async function restoreCameraAfterPresentation(): Promise<void> {
  presentationController.stop();
  const cameraTrack = localMedia?.getSendStream().getVideoTracks()[0] ?? null;
  await peerManager?.setOutgoingVideoTrack(cameraTrack);
  renderPresentationState();
}

function renderPresentationState(): void {
  const localPresenting =
    activePresentation?.presenterId === localParticipantId;
  const canStart = canStartScreenPresentation();
  videoGrid.classList.toggle("presenting", Boolean(activePresentation));
  for (const [participantId, tile] of tiles) {
    const presenter = activePresentation?.presenterId === participantId;
    tile.root.classList.toggle("presenter", presenter);
    if (presenter) {
      tile.placeholder.hidden = true;
    } else {
      tile.placeholder.hidden =
        participants.get(participantId)?.media.video ?? false;
    }
  }

  const localTile = tiles.get(localParticipantId);
  if (localPresenting && localTile) {
    processedCanvas.remove();
    presentationCanvas.hidden = false;
    if (presentationCanvas.parentElement !== localTile.root) {
      localTile.root.prepend(presentationCanvas);
    }
  } else {
    presentationCanvas.remove();
    presentationCanvas.hidden = true;
    if (localTile && processedCanvas.parentElement !== localTile.root) {
      localTile.root.prepend(processedCanvas);
    }
  }

  presentationToolbar.hidden = !activePresentation;
  presentationTools.hidden = !localPresenting;
  presentationLabel.textContent = activePresentation
    ? localPresenting
      ? "내 화면을 발표 중"
      : `${activePresentation.presenterName} 님의 화면 공유`
    : "화면 공유 중";
  presentationButton.disabled =
    !canStart ||
    presentationTransition ||
    Boolean(activePresentation && !localPresenting);
  setControlState(presentationButton, localPresenting);
  presentationButton.querySelector("span")!.textContent = !canStart
    ? activePresentation
      ? "시청 중"
      : "시청 전용"
    : localPresenting
      ? presentationTransition
        ? "종료 중…"
        : "공유 종료"
      : activePresentation
        ? "공유 중"
        : presentationTransition
          ? "준비 중…"
          : "화면 공유";
  presentationButton.title = !canStart
    ? "모바일에서는 다른 사람의 화면 공유를 볼 수만 있습니다."
    : activePresentation && !localPresenting
      ? `${activePresentation.presenterName} 님의 화면 공유를 보고 있습니다.`
      : "내 화면을 다른 참가자에게 공유";
  presentationButton.setAttribute(
    "aria-label",
    !canStart
      ? "모바일 화면 공유 시청 전용"
      : localPresenting
        ? "화면 공유 종료"
        : "화면 공유",
  );
  if (!localPresenting) {
    presentationController.setMarkerEnabled(false);
    highlighterButton.setAttribute("aria-pressed", "false");
    highlighterButton.textContent = "형광펜";
  }
  updateVideoGrid();
}

function canStartScreenPresentation(): boolean {
  return (
    !isMobileBrowser() &&
    typeof navigator.mediaDevices?.getDisplayMedia === "function"
  );
}

async function startAutomaticRecording(): Promise<void> {
  if (!pendingMeeting || !localMedia || !peerManager) return;
  if (pendingMeeting.recordingMode === "integrated" && !localIsHost) return;
  if (recorder) return;
  try {
    const nextRecorder = new MeetingRecorder(
      pendingMeeting.roomId,
      pendingMeeting.token,
      pendingMeeting.recordingMode === "integrated"
        ? recordingSources
        : localRecordingSources,
      () =>
        pendingMeeting?.recordingMode === "integrated"
          ? [
              ...(localMedia ? [localMedia.getSendStream()] : []),
              ...peerManager!.getRemoteStreams().values(),
            ]
          : [...(localMedia ? [localMedia.getSendStream()] : [])],
      (active, message) => {
        if (!active || pendingMeeting?.recordingMode === "per_participant") {
          toast(message);
        }
      },
      () => {},
    );
    const recordingId = await nextRecorder.start();
    recorder = nextRecorder;
    if (pendingMeeting.recordingMode === "integrated") {
      peerManager.sendRecordingState(true, recordingId);
    }
  } catch (error) {
    toast(`자동 녹화를 시작하지 못했습니다. ${errorMessage(error)}`, true);
  }
}

function recordingSources(): RecordingSource[] {
  const presentation = presentationRecordingSources();
  if (presentation.length > 0) return presentation;
  const result = localRecordingSources();
  for (const [participantId, tile] of tiles) {
    if (participantId === localParticipantId || !tile.video) continue;
    result.push({
      media: tile.video,
      name: participants.get(participantId)?.name ?? "참가자",
      visible: participants.get(participantId)?.media.video ?? true,
    });
  }
  return result;
}

function localRecordingSources(): RecordingSource[] {
  const presentation = presentationRecordingSources();
  if (presentation.length > 0) return presentation;
  const result: RecordingSource[] = [];
  if (pendingMeeting) {
    result.push({
      media: processedCanvas,
      name:
        participants.get(localParticipantId)?.name ?? pendingMeeting.name,
      visible: localMedia?.isVideoEnabled() ?? false,
    });
  }
  return result;
}

function presentationRecordingSources(): RecordingSource[] {
  if (!activePresentation) return [];
  if (activePresentation.presenterId === localParticipantId) {
    return presentationController.isActive()
      ? [{
          media: presentationCanvas,
          name: `${activePresentation.presenterName} · 화면 공유`,
          visible: true,
          fit: "contain",
        }]
      : [];
  }
  const tile = tiles.get(activePresentation.presenterId);
  return tile?.video
    ? [{
        media: tile.video,
        name: `${activePresentation.presenterName} · 화면 공유`,
        visible: true,
        fit: "contain",
      }]
    : [];
}

async function downloadTranscript(): Promise<void> {
  if (!pendingMeeting) return;
  try {
    const response = await fetch(
      `/api/rooms/${encodeURIComponent(pendingMeeting.roomId)}/transcripts?format=txt`,
      { headers: { Authorization: `Bearer ${pendingMeeting.token}` } },
    );
    if (!response.ok) throw new Error("회의록을 내려받지 못했습니다.");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${pendingMeeting.roomId}-transcript.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    toast(errorMessage(error), true);
  }
}

async function loadMeetingSummary(): Promise<void> {
  if (!pendingMeeting) return;
  try {
    const response = await fetch(
      `/api/rooms/${encodeURIComponent(pendingMeeting.roomId)}/summary`,
      { headers: { Authorization: `Bearer ${pendingMeeting.token}` } },
    );
    if (response.status === 204) return;
    const payload = (await response.json()) as MeetingSummaryResponse & {
      error?: string;
    };
    if (!response.ok) {
      throw new Error(payload.error ?? "회의 요약을 불러오지 못했습니다.");
    }
    latestMeetingSummary = payload;
    summaryEmpty.querySelector("strong")!.textContent =
      "저장된 AI 회의록이 있습니다.";
    summaryEmpty.querySelector("p")!.textContent =
      "입력 내용을 확인하면 기존 결과를 보완하거나 새로 생성할 수 있습니다.";
  } catch (error) {
    toast(errorMessage(error), true);
  }
}

async function openMeetingSummaryDialog(): Promise<void> {
  if (!pendingMeeting || !localIsHost) return;
  setSummaryDialogView("input");
  summaryEmailInput.value = currentUser?.email ?? "";
  summaryEmailStatus.textContent = "";
  summaryEmailStatus.className = "";
  if (!summaryDialog.open) summaryDialog.showModal();
  await loadSummaryEditorSource();
}

async function loadSummaryEditorSource(): Promise<void> {
  if (
    !pendingMeeting ||
    summaryTranscriptRoomId === pendingMeeting.roomId
  ) {
    return;
  }
  summaryTranscriptInput.disabled = true;
  summaryTranscriptInput.placeholder = "저장된 최종 자막을 불러오는 중입니다.";
  summaryTranscriptCount.textContent = "불러오는 중";
  summaryInputStatus.textContent = "회의 원문을 불러오고 있습니다.";
  try {
    const response = await fetch(
      `/api/rooms/${encodeURIComponent(pendingMeeting.roomId)}/transcripts`,
      { headers: { Authorization: `Bearer ${pendingMeeting.token}` } },
    );
    const payload = (await response.json()) as {
      transcripts?: Array<{
        speaker: string;
        text: string;
        createdAt: string;
      }>;
      error?: string;
    };
    if (!response.ok) {
      throw new Error(payload.error ?? "회의 원문을 불러오지 못했습니다.");
    }
    const transcripts = payload.transcripts ?? [];
    summaryTranscriptInput.value = transcripts
      .map(
        (item) =>
          `[${new Date(item.createdAt).toLocaleString("ko-KR")}] ${item.speaker}: ${item.text}`,
      )
      .join("\n");
    if (!summaryParticipantsInput.value) {
      summaryParticipantsInput.value = [
        ...new Set(transcripts.map((item) => item.speaker)),
      ].join(", ");
    }
    summaryTranscriptCount.textContent =
      `자막 ${transcripts.length}개 · ${summaryTranscriptInput.value.length.toLocaleString("ko-KR")}자`;
    summaryInputStatus.textContent = transcripts.length
      ? latestMeetingSummary
        ? "저장된 요약이 있습니다. 보완 내용을 확인하고 새로 만들 수 있습니다."
        : "자동 자막을 채웠습니다. 잘못 들린 표현과 빠진 내용을 확인해 주세요."
      : "저장된 자막이 없습니다. 실제 발언 내용을 직접 입력해 주세요.";
    summaryTranscriptRoomId = pendingMeeting.roomId;
  } catch (error) {
    summaryTranscriptCount.textContent = "불러오기 실패";
    summaryInputStatus.textContent = errorMessage(error);
    toast(errorMessage(error), true);
  } finally {
    summaryTranscriptInput.disabled = false;
    summaryTranscriptInput.placeholder = "실제 발언 내용을 입력해 주세요.";
  }
}

async function generateMeetingSummary(): Promise<void> {
  if (!pendingMeeting) return;
  const transcript = summaryTranscriptInput.value.trim();
  if (!transcript) {
    summaryTranscriptInput.focus();
    summaryInputStatus.textContent =
      "요약할 실제 발언 내용을 한 줄 이상 입력해 주세요.";
    return;
  }
  summarySubmitButton.disabled = true;
  regenerateSummaryButton.disabled = true;
  editSummaryInputButton.disabled = true;
  sendSummaryEmailButton.disabled = true;
  summarySubmitButton.textContent = "AI가 회의록을 정리하는 중…";
  regenerateSummaryButton.textContent = "AI가 다시 정리하는 중…";
  summaryInputStatus.textContent =
    "입력한 원문과 보완 내용을 기준으로 새 결과를 만들고 있습니다.";
  try {
    const response = await fetch(
      `/api/rooms/${encodeURIComponent(pendingMeeting.roomId)}/summary`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${pendingMeeting.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          participants: summaryParticipantsInput.value.trim(),
          meetingContext: summaryMeetingContextInput.value.trim(),
          guidance: summaryGuidanceInput.value.trim(),
          transcript,
          force: true,
        }),
      },
    );
    const payload = (await response.json()) as MeetingSummaryResponse & {
      error?: string;
    };
    if (!response.ok) {
      throw new Error(payload.error ?? "AI 회의 요약을 생성하지 못했습니다.");
    }
    latestMeetingSummary = payload;
    renderMeetingSummary(payload);
    setSummaryDialogView("result");
    summaryEmpty.querySelector("strong")!.textContent =
      "검토할 AI 회의록이 준비됐습니다.";
    summaryEmpty.querySelector("p")!.textContent =
      "팝업을 다시 열어 결과를 보완·재생성하거나 메일로 보낼 수 있습니다.";
    toast("AI 회의록을 새로 생성했습니다.");
  } catch (error) {
    summaryInputStatus.textContent = errorMessage(error);
    toast(errorMessage(error), true);
  } finally {
    summarySubmitButton.disabled = false;
    regenerateSummaryButton.disabled = false;
    editSummaryInputButton.disabled = false;
    sendSummaryEmailButton.disabled = false;
    summarySubmitButton.textContent = "이 내용으로 AI 회의록 만들기";
    regenerateSummaryButton.textContent = "같은 내용으로 다시 생성";
  }
}

function resetMeetingSummary(): void {
  latestMeetingSummary = undefined;
  summaryTranscriptRoomId = "";
  summaryParticipantsInput.value = "";
  summaryMeetingContextInput.value = "";
  summaryGuidanceInput.value = "";
  summaryTranscriptInput.value = "";
  summaryTranscriptCount.textContent = "불러오는 중";
  summaryEmailStatus.textContent = "";
  summaryEmailStatus.className = "";
  if (summaryDialog.open) summaryDialog.close();
  summaryEmpty.querySelector("strong")!.textContent =
    "보완한 내용으로 요약하세요.";
  summaryEmpty.querySelector("p")!.textContent =
    "참석자, 회의 성격, 실제 발언과 빠진 내용을 확인한 뒤 결과를 별도 팝업에서 검토할 수 있습니다.";
}

function renderMeetingSummary(payload: MeetingSummaryResponse): void {
  byId<HTMLElement>("summary-title").textContent = payload.summary.title;
  byId<HTMLElement>("summary-overview").textContent = payload.summary.overview;
  byId<HTMLElement>("summary-meta").textContent =
    `${payload.model} · 자막 ${payload.transcriptCount}개 · ${new Date(payload.generatedAt).toLocaleString("ko-KR")}`;
  fillSummaryTextList("summary-key-points", payload.summary.keyPoints);
  fillSummaryItemList("summary-decisions", payload.summary.decisions);
  fillSummaryItemList("summary-actions", payload.summary.actionItems);
  fillSummaryTextList("summary-questions", payload.summary.openQuestions);
  summaryEmailInput.value = currentUser?.email ?? "";
  summaryEmailStatus.textContent = "";
  summaryEmailStatus.className = "";
}

function setSummaryDialogView(view: "input" | "result" | "email"): void {
  const showInput = view === "input";
  summaryInputView.hidden = !showInput;
  summaryResultView.hidden = showInput;
  for (const step of document.querySelectorAll<HTMLElement>(
    "[data-summary-step]",
  )) {
    step.classList.toggle("active", step.dataset.summaryStep === view);
  }
  const title = byId<HTMLElement>("summary-dialog-title");
  const description = byId<HTMLElement>("summary-dialog-description");
  if (showInput) {
    title.textContent = "회의록에 맥락을 더해 주세요.";
    description.textContent =
      "자동 자막을 원문으로 채웠습니다. 잘못 들린 말과 빠진 맥락을 고치면 그 내용을 기준으로 새 회의록을 만듭니다.";
    return;
  }
  title.textContent =
    view === "email"
      ? "검토한 회의록을 메일로 보냈습니다."
      : "AI 회의록을 검토해 주세요.";
  description.textContent =
    view === "email"
      ? "같은 결과를 다시 보내거나, 입력 내용을 보완해 새 회의록을 만들 수 있습니다."
      : "결과가 부족하면 입력 내용을 보완하거나 같은 내용으로 AI를 다시 실행하세요.";
}

async function sendMeetingSummaryEmail(): Promise<void> {
  if (!pendingMeeting || !latestMeetingSummary) return;
  sendSummaryEmailButton.disabled = true;
  sendSummaryEmailButton.textContent = "메일을 보내는 중…";
  summaryEmailStatus.textContent = "검토한 최신 결과를 전송하고 있습니다.";
  summaryEmailStatus.className = "";
  try {
    const response = await fetch(
      `/api/rooms/${encodeURIComponent(pendingMeeting.roomId)}/summary/email`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${pendingMeeting.token}` },
      },
    );
    const payload = (await response.json()) as {
      message?: string;
      error?: string;
    };
    if (!response.ok) {
      throw new Error(payload.error ?? "회의록 메일을 보내지 못했습니다.");
    }
    summaryEmailStatus.textContent =
      payload.message ?? `${summaryEmailInput.value}로 전송했습니다.`;
    summaryEmailStatus.className = "success";
    setSummaryDialogView("email");
    toast("AI 회의록을 가입 이메일로 보냈습니다.");
  } catch (error) {
    summaryEmailStatus.textContent = errorMessage(error);
    summaryEmailStatus.className = "error";
    toast(errorMessage(error), true);
  } finally {
    sendSummaryEmailButton.disabled = false;
    sendSummaryEmailButton.textContent = "이 결과를 메일로 보내기";
  }
}

function fillSummaryTextList(id: string, items: string[]): void {
  const list = byId<HTMLUListElement>(id);
  list.replaceChildren();
  for (const text of items.length ? items : ["기록된 내용 없음"]) {
    const item = document.createElement("li");
    item.textContent = text;
    list.append(item);
  }
}

function fillSummaryItemList(id: string, items: MeetingSummaryItem[]): void {
  const list = byId<HTMLUListElement>(id);
  list.replaceChildren();
  if (items.length === 0) {
    const item = document.createElement("li");
    item.textContent = "기록된 내용 없음";
    list.append(item);
    return;
  }
  for (const summaryItem of items) {
    const item = document.createElement("li");
    const text = document.createElement("span");
    text.textContent = summaryItem.text;
    item.append(text);
    if (summaryItem.owner || summaryItem.dueDate) {
      const meta = document.createElement("small");
      meta.className = "summary-item-meta";
      meta.textContent = [
        summaryItem.owner ? `담당 ${summaryItem.owner}` : "",
        summaryItem.dueDate ? `기한 ${summaryItem.dueDate}` : "",
      ]
        .filter(Boolean)
        .join(" · ");
      item.append(meta);
    }
    list.append(item);
  }
}

async function copyInvite(): Promise<void> {
  if (!pendingMeeting) return;
  try {
    await navigator.clipboard.writeText(pendingMeeting.inviteUrl);
    toast("초대 링크를 복사했습니다.");
  } catch {
    toast("초대 링크를 복사하지 못했습니다.", true);
  }
}

function sendTypedSpeech(): void {
  const text = typedSpeechInput.value.replace(/\s+/g, " ").trim();
  if (!text || !peerManager) return;
  peerManager.sendCaption(
    text,
    true,
    typedCaptionSequence,
    "typed",
    ttsController.getVoiceStyle(),
  );
  typedCaptionSequence += 1;
  typedSpeechInput.value = "";
  typedSpeechCount.textContent = "0/500";
}

async function speakTypedCaption(
  text: string,
  voiceStyle?: TtsVoiceStyle,
): Promise<void> {
  if (!ttsController.isEnabled()) return;
  const spoken = await ttsController.speak(
    text,
    voiceStyle ?? ttsController.getVoiceStyle(),
  );
  if (!spoken && !ttsUnavailableNotified) {
    ttsUnavailableNotified = true;
    toast(
      "이 브라우저에 로컬 한국어 음성이 없어 입력 문장을 자막으로만 표시합니다.",
      true,
    );
  }
}

function updateTtsToggle(): void {
  const enabled = ttsController.isEnabled();
  typedSpeechToggle.setAttribute("aria-pressed", String(enabled));
  typedSpeechToggle.textContent = enabled ? "자동 읽기 켬" : "자동 읽기 끔";
  if (enabled) ttsUnavailableNotified = false;
}

function updateCaptionToggle(enabled: boolean): void {
  const button = byId<HTMLButtonElement>("toggle-captions");
  setControlState(button, enabled);
  button.textContent = enabled ? "내 자막 켬" : "내 자막 끔";
}

function setMediaAvailability(hasAudio: boolean, hasVideo: boolean): void {
  const microphoneButton = byId<HTMLButtonElement>("toggle-mic");
  const cameraButton = byId<HTMLButtonElement>("toggle-camera");
  const effectsButton = byId<HTMLButtonElement>("toggle-effects");
  microphoneButton.disabled = !hasAudio;
  cameraButton.disabled = !hasVideo;
  effectsButton.disabled = !hasVideo;
  setControlState(microphoneButton, localMedia?.isAudioEnabled() ?? hasAudio);
  setControlState(cameraButton, localMedia?.isVideoEnabled() ?? hasVideo);
  syncEffectControls();
  for (const button of document.querySelectorAll<HTMLButtonElement>(
    "#effect-control button, #avatar-row button, #meeting-effect-control button, #meeting-avatar-row button",
  )) {
    button.disabled = !hasVideo;
  }
}

async function leaveMeeting(): Promise<void> {
  try {
    if (recorder) {
      await recorder.stop();
      recorder = undefined;
      if (pendingMeeting?.recordingMode === "integrated") {
        peerManager?.sendRecordingState(false);
      }
    }
  } catch (error) {
    toast(errorMessage(error), true);
  }
  speechController?.disable();
  ttsController.stop();
  if (activePresentation?.presenterId === localParticipantId) {
    peerManager?.sendPresentationState(false);
  }
  presentationController.stop();
  activePresentation = undefined;
  peerManager?.close();
  localMedia?.stop();
  window.clearInterval(meetingTimer);
  window.clearTimeout(overlayCaptionTimer);
  for (const timer of captionSpeakerTimers.values()) window.clearTimeout(timer);
  location.href = "/";
}

function setEffect(mode: VideoEffectMode): void {
  localMedia?.setEffect(mode);
  previewModeLabel.textContent =
    mode === "none"
      ? "원본 영상"
      : mode === "avatar"
        ? "얼굴 추적 아바타"
        : mode === "blur"
          ? "배경 블러"
          : "아바타 + 배경 블러";
  syncEffectControls();
  broadcastMediaState();
}

function selectAvatar(avatar: string): void {
  currentAvatar = avatar;
  localMedia?.setAvatar(currentAvatar);
  byId<HTMLElement>("placeholder-avatar").textContent = currentAvatar;
  for (const button of document.querySelectorAll<HTMLButtonElement>(
    "[data-avatar]",
  )) {
    button.classList.toggle("active", button.dataset.avatar === currentAvatar);
  }
}

function syncEffectControls(): void {
  const effect = localMedia?.getEffect() ?? "none";
  for (const button of document.querySelectorAll<HTMLButtonElement>(
    "#effect-control button, #meeting-effect-control button",
  )) {
    button.classList.toggle("active", button.dataset.effect === effect);
  }
  const effectsButton = byId<HTMLButtonElement>("toggle-effects");
  const effectLabel =
    effect === "none"
      ? "사용 안 함"
      : effect === "avatar"
        ? `${currentAvatar} 아바타`
        : effect === "blur"
          ? "배경 블러"
          : `${currentAvatar} 아바타 + 배경 블러`;
  effectsButton.dataset.effect = effect;
  effectsButton.setAttribute("aria-label", `화면 효과 설정, 현재 ${effectLabel}`);
  effectsButton.title = `화면 효과 · ${effectLabel}`;
  setControlState(effectsButton, effect !== "none");
}

function loadDeviceSettings(): DeviceSettings {
  const fallback: DeviceSettings = {
    audioInputId: "",
    videoInputId: "",
    outputDeviceId: "",
    inputGain: 1,
    outputVolume: 1,
    channelMode: "mono",
  };
  try {
    const stored = JSON.parse(
      localStorage.getItem("softi-meet-device-settings") ?? "{}",
    ) as Partial<DeviceSettings>;
    return {
      audioInputId: stored.audioInputId ?? fallback.audioInputId,
      videoInputId: stored.videoInputId ?? fallback.videoInputId,
      outputDeviceId: stored.outputDeviceId ?? fallback.outputDeviceId,
      inputGain:
        typeof stored.inputGain === "number"
          ? Math.min(2, Math.max(0, stored.inputGain))
          : fallback.inputGain,
      outputVolume:
        typeof stored.outputVolume === "number"
          ? Math.min(1, Math.max(0, stored.outputVolume))
          : fallback.outputVolume,
      channelMode:
        stored.channelMode === "stereo" ? "stereo" : fallback.channelMode,
    };
  } catch {
    return fallback;
  }
}

function saveDeviceSettings(): void {
  localStorage.setItem(
    "softi-meet-device-settings",
    JSON.stringify(deviceSettings),
  );
}

function syncDeviceControls(): void {
  const values: Record<string, string> = {
    inputGain: String(Math.round(deviceSettings.inputGain * 100)),
    outputVolume: String(Math.round(deviceSettings.outputVolume * 100)),
    channelMode: deviceSettings.channelMode,
  };
  for (const control of document.querySelectorAll<
    HTMLInputElement | HTMLSelectElement
  >("[data-device-setting]")) {
    const key = control.dataset.deviceSetting ?? "";
    if (values[key] !== undefined) control.value = values[key];
  }
  for (const output of document.querySelectorAll<HTMLOutputElement>(
    '[data-setting-output="inputGain"]',
  )) {
    output.textContent = `${Math.round(deviceSettings.inputGain * 100)}%`;
  }
  for (const output of document.querySelectorAll<HTMLOutputElement>(
    '[data-setting-output="outputVolume"]',
  )) {
    output.textContent = `${Math.round(deviceSettings.outputVolume * 100)}%`;
  }
  for (const select of document.querySelectorAll<HTMLSelectElement>(
    '[data-device-select="audioinput"]',
  )) {
    select.value = deviceSettings.audioInputId;
  }
  for (const select of document.querySelectorAll<HTMLSelectElement>(
    '[data-device-select="videoinput"]',
  )) {
    select.value = deviceSettings.videoInputId;
  }
  for (const select of document.querySelectorAll<HTMLSelectElement>(
    '[data-device-select="audiooutput"]',
  )) {
    select.value = deviceSettings.outputDeviceId;
  }
}

async function refreshDeviceLists(): Promise<void> {
  if (!navigator.mediaDevices?.enumerateDevices) return;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const labels = {
      audioinput: "기본 마이크",
      videoinput: "기본 카메라",
      audiooutput: "기본 스피커",
    };
    for (const kind of [
      "audioinput",
      "videoinput",
      "audiooutput",
    ] as MediaDeviceKind[]) {
      const matches = devices.filter((device) => device.kind === kind);
      for (const select of document.querySelectorAll<HTMLSelectElement>(
        `[data-device-select="${kind}"]`,
      )) {
        const fragment = document.createDocumentFragment();
        const fallback = document.createElement("option");
        fallback.value = "";
        fallback.textContent = labels[kind];
        fragment.append(fallback);
        matches.forEach((device, index) => {
          const option = document.createElement("option");
          option.value = device.deviceId;
          option.textContent =
            device.label ||
            `${kind === "audioinput" ? "마이크" : kind === "videoinput" ? "카메라" : "스피커"} ${index + 1}`;
          fragment.append(option);
        });
        select.replaceChildren(fragment);
      }
    }
    syncDeviceControls();
  } catch {
    // 장치 열거를 막는 브라우저에서도 기본 장치로 회의를 계속할 수 있습니다.
  }
}

async function changeDevice(kind: string, deviceId: string): Promise<void> {
  if (!localMedia) return;
  try {
    if (kind === "audioinput") {
      await localMedia.switchAudioInput(deviceId);
      deviceSettings.audioInputId = deviceId;
      const track = localMedia.getSendStream().getAudioTracks()[0];
      if (track) await peerManager?.replaceLocalTrack("audio", track);
    } else if (kind === "videoinput") {
      await localMedia.switchVideoInput(deviceId);
      deviceSettings.videoInputId = deviceId;
    } else if (kind === "audiooutput") {
      deviceSettings.outputDeviceId = deviceId;
      localMedia.setOutputDevice(deviceId);
      applyOutputSettingsToRemoteVideos();
    }
    saveDeviceSettings();
    syncDeviceControls();
    await refreshDeviceLists();
    setMediaAvailability(localMedia.hasAudio(), localMedia.hasVideo());
    broadcastMediaState();
  } catch (error) {
    syncDeviceControls();
    toast(errorMessage(error), true);
  }
}

async function changeDeviceSetting(
  setting: string,
  rawValue: string,
): Promise<void> {
  if (setting === "inputGain") {
    deviceSettings.inputGain = Number(rawValue) / 100;
    localMedia?.setInputGain(deviceSettings.inputGain);
  } else if (setting === "outputVolume") {
    deviceSettings.outputVolume = Number(rawValue) / 100;
    localMedia?.setOutputVolume(deviceSettings.outputVolume);
    applyOutputSettingsToRemoteVideos();
  } else if (setting === "channelMode") {
    deviceSettings.channelMode = rawValue === "stereo" ? "stereo" : "mono";
    try {
      await localMedia?.setChannelMode(deviceSettings.channelMode);
    } catch {
      toast("이 마이크는 선택한 채널 구성을 지원하지 않습니다.", true);
    }
  }
  saveDeviceSettings();
  syncDeviceControls();
}

function applyOutputSettingsToRemoteVideos(): void {
  for (const tile of tiles.values()) {
    if (tile.video) applyOutputSettings(tile.video);
  }
}

function applyOutputSettings(media: HTMLMediaElement): void {
  media.volume = deviceSettings.outputVolume;
  const sinkMedia = media as HTMLMediaElement & {
    setSinkId?: (sinkId: string) => Promise<void>;
  };
  if (deviceSettings.outputDeviceId && sinkMedia.setSinkId) {
    void sinkMedia.setSinkId(deviceSettings.outputDeviceId).catch(() => {
      toast("선택한 스피커로 출력을 바꾸지 못했습니다.", true);
    });
  }
}

function resolveParticipantName(participantId: string, fallback: string): string {
  return (
    participants.get(participantId)?.name ??
    (participantId === localParticipantId ? pendingMeeting?.name : undefined) ??
    fallback
  );
}

function showCaptionOverlay(
  participantId: string,
  speaker: string,
  text: string,
  final: boolean,
): void {
  window.clearTimeout(overlayCaptionTimer);
  const speakerTile = tiles.get(participantId)?.root;
  const compactLayout = window.matchMedia("(max-width: 980px)").matches;
  const captionTarget = compactLayout ? captionOverlayHome : speakerTile;
  if (!captionTarget) {
    hideCaptionOverlay();
    return;
  }
  if (captionOverlay.parentElement !== captionTarget) {
    captionTarget.append(captionOverlay);
  }
  overlayCaptionSpeaker.textContent = speaker;
  overlayCaptionText.textContent = text;
  captionOverlay.hidden = false;
  captionOverlay.classList.toggle("interim", !final);
  if (final) {
    overlayCaptionTimer = window.setTimeout(hideCaptionOverlay, 5_500);
  }
}

function hideCaptionOverlay(): void {
  captionOverlay.hidden = true;
  captionOverlay.classList.remove("interim");
  if (captionOverlay.parentElement !== captionOverlayHome) {
    captionOverlayHome.append(captionOverlay);
  }
}

function markCaptionSpeaker(participantId: string): void {
  const tile = tiles.get(participantId)?.root;
  if (!tile) return;
  const previous = captionSpeakerTimers.get(participantId);
  if (previous) window.clearTimeout(previous);
  tile.classList.add("caption-speaker");
  captionSpeakerTimers.set(
    participantId,
    window.setTimeout(() => {
      tile.classList.remove("caption-speaker");
      captionSpeakerTimers.delete(participantId);
    }, 3_000),
  );
}

function currentMediaState(): MediaState {
  const effect = localMedia?.getEffect() ?? "none";
  return {
    audio: localMedia?.isAudioEnabled() ?? false,
    video: localMedia?.isVideoEnabled() ?? false,
    avatar: effect === "avatar" || effect === "both",
    blur: effect === "blur" || effect === "both",
  };
}

function broadcastMediaState(): void {
  peerManager?.sendMediaState(currentMediaState());
}

function updateMicLevel(level: AudioLevel): void {
  currentMicLevel = level;
  speechController?.setAudioLevel?.(level);
  const percent = Math.round(level.linear * 100);
  lobbyMicMeter.style.width = `${percent}%`;
  meetingMicMeter.style.width = `${percent}%`;
  dialogMicMeter.style.width = `${percent}%`;
  lobbyMicValue.textContent = level.silent
    ? "입력 없음"
    : level.clipping
      ? "클리핑"
      : `${Math.round(level.dbfs)} dBFS`;
  meetingMicValue.textContent = level.silent
    ? "무음"
    : `${Math.round(level.dbfs)}`;
  dialogMicValue.textContent = lobbyMicValue.textContent;
  const localTile = tiles.get(localParticipantId);
  if (localTile) {
    localTile.audio.style.setProperty("--level", `${percent}%`);
    localTile.root.classList.toggle("speaking", level.dbfs > -38);
  }
}

function updateCameraLevel(level: CameraLevel): void {
  const percent = Math.round(level.linear * 100);
  lobbyCameraMeter.style.width = `${percent}%`;
  meetingCameraMeter.style.width = `${percent}%`;
  dialogCameraMeter.style.width = `${percent}%`;
  lobbyCameraValue.textContent = level.label;
  meetingCameraValue.textContent =
    level.label === "꺼짐" ? "OFF" : `${percent}%`;
  dialogCameraValue.textContent = level.label;
}

function updateSpeakerAggregate(): void {
  const maximum = Math.max(0, ...[...remoteLevels.values()].map((level) => level.linear));
  const percent = Math.round(maximum * 100);
  meetingSpeakerMeter.style.width = `${percent}%`;
  meetingSpeakerValue.textContent = `${percent}%`;
}

function updateQuality(quality: ConnectionQuality): void {
  const orbit = byId<HTMLElement>("quality-orbit");
  orbit.className = `quality-orbit ${quality.grade === "waiting" ? "good" : quality.grade}`;
  byId<HTMLElement>("quality-label").textContent =
    quality.grade === "waiting"
      ? "측정 중"
      : quality.grade === "good"
        ? "좋음"
        : quality.grade === "fair"
          ? "보통"
          : "불안정";
  byId<HTMLElement>("quality-rtt").textContent =
    quality.rttMs === undefined ? "— ms" : `${quality.rttMs} ms`;
  byId<HTMLElement>("quality-jitter").textContent =
    quality.jitterMs === undefined ? "— ms" : `${quality.jitterMs} ms`;
  byId<HTMLElement>("quality-loss").textContent =
    quality.lossPercent === undefined ? "— %" : `${quality.lossPercent} %`;
  byId<HTMLElement>("quality-bitrate").textContent =
    quality.bitrateKbps === undefined ? "— kbps" : `${quality.bitrateKbps} kbps`;
  byId<HTMLElement>("quality-route").textContent =
    quality.route === "direct"
      ? "P2P 직접"
      : quality.route === "turn"
        ? "자체 TURN"
        : "확인 중";
  byId<HTMLElement>("quality-advice").textContent = quality.advice;
}

function updateNetworkState(
  state: "connecting" | "connected" | "reconnecting" | "closed",
): void {
  const labels = {
    connecting: "연결 중",
    connected: "보안 연결됨",
    reconnecting: "연결 복구 중",
    closed: "연결 종료",
  };
  globalNetworkState.classList.toggle("connected", state === "connected");
  globalNetworkState.lastChild!.textContent = ` ${labels[state]}`;
}

function updateMeetingTimer(): void {
  const totalSeconds = Math.max(
    0,
    Math.floor((Date.now() - meetingStartedAt) / 1000),
  );
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  timerElement.textContent =
    hours > 0
      ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function updateParticipantCount(): void {
  participantCount.textContent = `${participants.size} / 6명`;
  updateVideoGrid();
}

function updateVideoGrid(): void {
  videoGrid.dataset.count = String(activePresentation ? 1 : Math.max(1, tiles.size));
}

function setEntryBusy(busy: boolean): void {
  createButton.disabled = busy;
  refreshRoomsButton.disabled = busy;
  for (const button of roomList.querySelectorAll<HTMLButtonElement>(
    "button[data-room-id]",
  )) {
    button.disabled =
      busy || button.textContent?.includes("정원이 찼습니다") === true;
  }
}

function setControlState(button: HTMLButtonElement, active: boolean): void {
  button.classList.toggle("active", active);
  button.setAttribute("aria-pressed", String(active));
}

function showView(view: "auth" | "lobby" | "check" | "meeting"): void {
  authView.hidden = view !== "auth";
  lobbyView.hidden = view !== "lobby";
  checkView.hidden = view !== "check";
  meetingView.hidden = view !== "meeting";
  document.body.classList.toggle("meeting-active", view === "meeting");
  syncViewportHeight();
}

async function requestImmersiveMode(): Promise<void> {
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const mobile =
    window.matchMedia("(pointer: coarse)").matches &&
    window.matchMedia("(max-width: 980px)").matches;

  if (
    !mobile ||
    standalone ||
    document.fullscreenElement ||
    !document.documentElement.requestFullscreen
  ) {
    return;
  }

  try {
    await document.documentElement.requestFullscreen({ navigationUI: "hide" });
  } catch {
    // iOS Safari 등 전체 화면 API가 제한된 환경은 PWA 표시 모드로 대응합니다.
  }
}

function toast(message: string, error = false): void {
  const element = document.createElement("div");
  element.className = `toast${error ? " error" : ""}`;
  element.setAttribute("role", error ? "alert" : "status");

  const messageElement = document.createElement("span");
  messageElement.className = "toast-message";
  messageElement.textContent = message;

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "toast-close";
  closeButton.setAttribute("aria-label", "알림 닫기");
  closeButton.title = "알림 닫기";
  closeButton.textContent = "×";

  let timeout = 0;
  const dismiss = (): void => {
    window.clearTimeout(timeout);
    element.remove();
  };

  closeButton.addEventListener("click", dismiss);
  element.append(messageElement, closeButton);
  toastRegion.append(element);
  timeout = window.setTimeout(dismiss, 5_500);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "처리 중 오류가 발생했습니다.";
}

void currentMicLevel;
